import { FulfillmentGroupStatus } from "@redotech/redo-model/fulfillments/fulfillment-group-status";
import {
  inboundShipmentIdFormat,
  locationIdFormat,
  packingTemplateIdFormat,
  productFamilyIdFormat,
  productIdFormat,
  transferOrderFileIdFormat,
  transferOrderIdFormat,
  transferOrderProductIdFormat,
} from "@redotech/redo-model/ids";
import {
  PackingMode,
  PackingModeSchema,
} from "@redotech/redo-model/inventory/packing-mode";
import {
  transfer_order_kind,
  transfer_order_status,
} from "@redotech/redo-prisma-client/enums";
import * as zExt from "@redotech/rpc/ext";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod/v4";
import { InboundShipmentStatus } from "../shipments/get-shipments";

// FG order reference for building navigation links
const FulfillmentGroupOrderRefSchema_ = z.object({
  id: z.string(),
  redoOrderId: z.string().nullish(),
  name: z.string(),
});
export const FulfillmentGroupOrderRefSchema: z.ZodType<FulfillmentGroupOrderRef> =
  FulfillmentGroupOrderRefSchema_;
export interface FulfillmentGroupOrderRef
  extends z.infer<typeof FulfillmentGroupOrderRefSchema_> {}

// Summary of the fulfillment group linked to a transfer
const FulfillmentGroupSummarySchema_ = z.object({
  id: z.string(),
  status: createEnum(FulfillmentGroupStatus, {
    enumName: "FulfillmentGroupStatus",
  }),
  orders: z.array(FulfillmentGroupOrderRefSchema),
});
export const FulfillmentGroupSummarySchema: z.ZodType<FulfillmentGroupSummary> =
  FulfillmentGroupSummarySchema_;
export interface FulfillmentGroupSummary
  extends z.infer<typeof FulfillmentGroupSummarySchema_> {}

export const TransferOrderStatus = createEnum(transfer_order_status);
export type TransferOrderStatus = z.infer<typeof TransferOrderStatus>;

export const TransferOrderKind = createEnum(transfer_order_kind);
export type TransferOrderKind = z.infer<typeof TransferOrderKind>;

// Transfer product schema
const TransferProductSchema_ = z.object({
  id: transferOrderProductIdFormat.schema,
  productId: productIdFormat.schema,
  expectedQuantity: z.number().int(),
  originStockQuantity: z.number().int().nullable(),
  title: z.string(),
  sku: z.string().nullish(),
  image: z.string().nullish(),
  productFamilyId: productFamilyIdFormat.schema.nullish(),
  productFamilyTitle: z.string().nullish(),
  productFamilyImage: z.string().nullish(),
  packingMode: PackingModeSchema.default(PackingMode.INDIVIDUAL),
  packingTemplateId: packingTemplateIdFormat.schema.nullable().default(null),
  packingTemplateName: z.string().nullable().default(null),
  enteredQuantity: z.number().int().default(0),
  unitsPerBox: z.number().nullable().default(null),
});

export const TransferProductSchema: z.ZodType<TransferProduct> =
  TransferProductSchema_;
export interface TransferProduct
  extends z.infer<typeof TransferProductSchema_> {}

// Transfer file schema
export const TransferFileSchema_ = z.object({
  id: transferOrderFileIdFormat.schema,
  title: z.string(),
  url: z.string(),
  contentType: z.string(),
  createdBy: z.object({ name: z.string(), email: z.string() }),
  createdAt: zExt.instant(),
});

export const TransferFileSchema: z.ZodType<TransferFile> = TransferFileSchema_;
export interface TransferFile extends z.infer<typeof TransferFileSchema_> {}

// Inbound shipment summary for transfer
const TransferShipmentSummarySchema_ = z.object({
  id: inboundShipmentIdFormat.schema,
  shipmentNumber: z.string().nullish(),
  status: createEnum([
    InboundShipmentStatus.PENDING,
    InboundShipmentStatus.IN_TRANSIT,
    InboundShipmentStatus.PARTIALLY_RECEIVED,
    InboundShipmentStatus.RECEIVED,
  ]),
  carrier: z.string().nullish(),
  trackingNumber: z.string().nullish(),
  trackingUrl: z.string().nullish(),
  estimatedArrivalDate: zExt.plainDate().nullish(),
  skuCount: z.number().int(),
  itemCount: z.number().int(),
  receivedCount: z.number().int(),
  createdAt: zExt.instant(),
});

export const TransferShipmentSummarySchema: z.ZodType<TransferShipmentSummary> =
  TransferShipmentSummarySchema_;
export interface TransferShipmentSummary
  extends z.infer<typeof TransferShipmentSummarySchema_> {}

// Location summary for transfer
const TransferLocationSchema_ = z.object({
  id: locationIdFormat.schema,
  name: z.string(),
});

export const TransferLocationSchema: z.ZodType<TransferLocation> =
  TransferLocationSchema_;
export interface TransferLocation
  extends z.infer<typeof TransferLocationSchema_> {}

// Complete transfer details
const TransferDetailsSchema_ = z.object({
  id: transferOrderIdFormat.schema,
  transferNumber: z.string(),
  originLocation: TransferLocationSchema,
  destinationLocation: TransferLocationSchema,
  status: TransferOrderStatus,
  kind: TransferOrderKind,
  notes: z.string().nullish(),
  tags: z.array(z.string()),
  products: z.array(TransferProductSchema),
  files: z.array(TransferFileSchema),
  fulfillmentGroups: z.array(FulfillmentGroupSummarySchema).nullish(),
  shipments: z.array(TransferShipmentSummarySchema),
  createdBy: z.object({ name: z.string(), email: z.string() }),
  createdAt: zExt.instant(),
  updatedAt: zExt.instant(),
});

export const TransferDetailsSchema: z.ZodType<TransferDetails> =
  TransferDetailsSchema_;
export interface TransferDetails
  extends z.infer<typeof TransferDetailsSchema_> {}

// List item schema (lighter weight for lists)
const TransferListItemSchema_ = z.object({
  id: transferOrderIdFormat.schema,
  transferNumber: z.string(),
  originLocation: TransferLocationSchema,
  destinationLocation: TransferLocationSchema,
  status: TransferOrderStatus,
  kind: TransferOrderKind,
  skuCount: z.number().int(),
  totalItems: z.number().int(),
  fulfillmentGroupId: z.string().nullish(),
  createdAt: zExt.instant(),
  updatedAt: zExt.instant(),
});

export const TransferListItemSchema: z.ZodType<TransferListItem> =
  TransferListItemSchema_;
export interface TransferListItem
  extends z.infer<typeof TransferListItemSchema_> {}
